import React, { useMemo, useState } from "react";
import Icon from "@mdi/react";
import {
  mdiAccount,
  mdiAlertOutline,
  mdiBarcodeScan,
  mdiCalendar,
  mdiCartOutline,
  mdiCheckCircle,
  mdiClipboardCheckOutline,
  mdiClose,
  mdiEyeOutline,
  mdiMapMarker,
  mdiPackageVariant,
  mdiRefresh,
  mdiRotateLeft,
  mdiSwapHorizontal,
} from "@mdi/js";
import { useHistory } from "react-router-dom";
import {
  AwaitingReturnItemModel,
  AwaitingReturnModel,
} from "../model/warehouse_inward_models";
import {
  isReplacementStockTransferSubtype,
  normalizeStockTransferSubtype,
  ReturnableDestinationType,
  StockTransferSubtype,
  WarehouseOutwardStatus,
} from "../model/warehouse_outward_model";
import {
  formatAmountWithCommas,
  formatDateYYYYMMDD,
} from "../../content_panel/utils/helpers";
import { urls } from "../../../../utils/urls";
import ConvertToSOPriceModal from "../stock-transfer/outward_view/ConvertToSOPriceModal";
import { ConvertSTToSO } from "../warehouse_module_repo";
import {
  ToastType,
  useToast,
} from "../../../../context_providers/toast/toast_ctx";
import "../inward-styles.css";
import { getInwardStatusClass } from "../warehouse_helper";

interface AwaitingReturnsCardListProps {
  awaitingReturns: AwaitingReturnModel[];
  updatesAllowed?: boolean;
}

interface DialogProps {
  row: AwaitingReturnModel;
  onClose: () => void;
  onRecordReceipt: (row: AwaitingReturnModel) => void;
}

const subtypePillMap: Record<string, string> = {
  replacement: "bg-red-100 text-red-700 border border-red-300",
  returnable: "bg-blue-100 text-blue-700 border border-blue-300",
};

const getPartyName = (row: AwaitingReturnModel) => {
  return row.party_name || row.customer_name || row.vendor_name || "--";
};

const getLinkedRef = (row: AwaitingReturnModel) => {
  return (
    row.linked_reference || row.linked_order_id || row.complaint_id || "--"
  );
};

const getDueDate = (row: AwaitingReturnModel) => {
  return row.due_date || row.expected_return_date || row.expected_date || "";
};

const getItemValue = (item: AwaitingReturnItemModel) => {
  return (item.requestedQty || 0) * (item.itemPrice || 0);
};

const getSubtypeLabel = (subType?: string) => {
  const normalized = normalizeStockTransferSubtype(subType);
  if (normalized === StockTransferSubtype.REPLACEMENT) {
    return StockTransferSubtype.REPLACEMENT;
  }
  if (normalized === StockTransferSubtype.RETURNABLE) {
    return StockTransferSubtype.RETURNABLE;
  }
  if (normalized === StockTransferSubtype.REGULAR) {
    return StockTransferSubtype.REGULAR;
  }
  return subType || "ST";
};

const getReplacementInwardStatus = (row: AwaitingReturnModel) => {
  const sourceStatus = row.inward_status || row.status || "pending";
  const normalized = sourceStatus.toLowerCase();
  if (normalized === "received") return "completed";
  return normalized;
};

const ReturnableDialog: React.FC<DialogProps> = ({
  row,
  onClose,
  onRecordReceipt,
}) => {
  const [showConvertModal, setShowConvertModal] = useState(false);
  const { showToast } = useToast();
  const history = useHistory();
  const dueDate = getDueDate(row);
  const overdueDays = row.overdue_days || 0;
  const totalOrdered = row.totalOrderedQty || 0;
  const totalReceived = row.totalReceivedQty || 0;
  const progress =
    totalOrdered > 0 ? Math.round((totalReceived / totalOrdered) * 100) : 0;

  return (
    <>
      <div
        id="warehouse-inward-awaiting-returnable-modal"
        className="fixed inset-0 bg-black bg-opacity-50 z-50 overflow-y-auto flex items-start justify-center p-4 py-10"
      >
        <div className="relative bg-white rounded-lg shadow-lg w-full max-w-4xl p-4 wh-modal-xs">
          <button
            id="warehouse-inward-awaiting-returnable-modal-close"
            className="absolute right-4 top-4"
            onClick={onClose}
          >
            <Icon path={mdiClose} size={0.8} />
          </button>

          <div className="flex items-center gap-3 flex-wrap pr-8">
            <h2 className="font-semibold text-xl">{row.id}</h2>

            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 text-xs rounded-full bg-blue-100 text-blue-600 border border-blue-300">
              <Icon path={mdiRotateLeft} size={0.6} />
              Returnable
            </span>

            {overdueDays > 0 && (
              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 text-xs rounded-full bg-red-100 text-red-600">
                <Icon path={mdiAlertOutline} size={0.6} />
                Overdue
              </span>
            )}
          </div>

          {overdueDays > 0 && (
            <div className="mt-6 flex gap-3 rounded-lg border border-red-300 bg-red-50 p-4">
              <Icon
                path={mdiAlertOutline}
                size={1}
                className="text-red-600 mt-1"
              />
              <div className="text-sm">
                <p className="font-medium text-red-600">
                  Overdue by {overdueDays} days
                </p>
                <p className="text-muted-foreground mt-1">
                  Expected return date was{" "}
                  {dueDate ? formatDateYYYYMMDD(dueDate) : "--"}.
                </p>
              </div>
            </div>
          )}

          <div className="mt-6 rounded-lg border bg-card p-4">
            <div className="flex gap-3 items-center">
              <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
                <Icon path={mdiCartOutline} size={0.8} className="text-white" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">
                  Original Reference
                </p>
                <p className="font-semibold">{getLinkedRef(row)}</p>
              </div>
            </div>
          </div>

          <div className="mt-6 rounded-lg border bg-card p-6">
            <h3 className="flex items-center gap-2 font-medium text-sm mb-3">
              <Icon path={mdiMapMarker} size={0.8} className="text-green-600" />
              Customer / Vendor Details
            </h3>
            <p className="font-semibold">{getPartyName(row)}</p>
            <p className="text-sm text-muted-foreground">
              {row.vendor_warehouse_address?.line1 ||
                row.shipping_address?.line1 ||
                "Address not available"}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4 mt-6">
            <div className="rounded-lg border bg-card p-4">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Icon
                  path={mdiCalendar}
                  size={0.8}
                  className="text-purple-600"
                />
                Expected Return
              </div>
              <p className="font-semibold text-red-600">
                {dueDate ? formatDateYYYYMMDD(dueDate) : "--"}
              </p>
            </div>

            <div className="rounded-lg border bg-card p-4">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Icon path={mdiAccount} size={0.8} className="text-teal-600" />
                Requested By
              </div>
              <p className="font-semibold">{row.created_by_name || "--"}</p>
            </div>
          </div>

          <div className="mt-6 rounded-lg border bg-card p-6">
            <div className="flex justify-between mb-3">
              <h3 className="flex items-center gap-2 text-sm font-medium">
                <Icon
                  path={mdiRotateLeft}
                  size={0.8}
                  className="text-blue-600"
                />
                Return Progress
              </h3>
              <span className="font-semibold text-sm">{progress}%</span>
            </div>

            <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
              <div
                className="bg-white h-2 rounded-full"
                style={{ width: `${progress}%` }}
              />
            </div>

            <p className="text-sm text-muted-foreground">
              {totalReceived} of {totalOrdered} items returned
            </p>
          </div>

          <div className="mt-6 rounded-lg border bg-card p-6">
            <h3 className="flex items-center gap-2 text-sm font-medium mb-4">
              <Icon
                path={mdiPackageVariant}
                size={0.8}
                className="text-orange-600"
              />
              Items ({(row.items || []).length})
            </h3>

            <div className="space-y-3">
              {(row.items || []).map((item) => {
                const returnedForItem = Math.min(
                  item.requestedQty || 0,
                  totalReceived,
                );

                return (
                  <div
                    className="border rounded-lg p-3"
                    key={`${row.id}-${item.inventoryItemCode}`}
                  >
                    <div className="flex justify-between">
                      <div>
                        <p className="font-medium">{item.itemName}</p>
                        <p className="text-xs text-muted-foreground">
                          {item.inventoryItemCode}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold">
                          Rs {formatAmountWithCommas(getItemValue(item))}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {returnedForItem}/{item.requestedQty} returned
                        </p>
                      </div>
                    </div>

                    {item.deviceIds && item.deviceIds.length > 0 && (
                      <div className="mt-3 border-t pt-3">
                        <p className="text-xs flex items-center gap-1 mb-2">
                          <Icon
                            path={mdiBarcodeScan}
                            size={0.6}
                            className="text-blue-600"
                          />
                          Device IDs ({item.deviceIds.length})
                        </p>

                        <div className="flex flex-wrap gap-2">
                          {item.deviceIds.map((deviceId, idx) => (
                            <span
                              key={`${item.inventoryItemCode}-${deviceId}`}
                              className={`px-2 py-0.5 text-xs rounded-full flex items-center gap-1 ${
                                idx < returnedForItem
                                  ? "bg-green-100 text-green-700"
                                  : "border"
                              }`}
                            >
                              {deviceId}
                              {idx < returnedForItem && (
                                <Icon path={mdiCheckCircle} size={0.5} />
                              )}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {row.destination_type === ReturnableDestinationType.CUSTOMER &&
            !row.converted_order_id &&
            row.status === WarehouseOutwardStatus.DISPATCHED && (
              <div className="mt-6 flex flex-wrap gap-2">
                <button
                  id="warehouse-inward-awaiting-returnable-modal-convert-so-btn"
                  className="border px-2 py-2 rounded-md flex items-center gap-2 hover:bg-gray-100"
                  type="button"
                  onClick={() => setShowConvertModal(true)}
                >
                  <Icon path={mdiSwapHorizontal} size={0.8} />
                  Convert to SO
                </button>
              </div>
            )}

          <div className="mt-6 flex justify-between border-t pt-4">
            <span className="text-muted-foreground">Total Value</span>
            <span className="text-xl font-bold">
              Rs{" "}
              {formatAmountWithCommas(
                (row.items || []).reduce(
                  (sum, item) => sum + getItemValue(item),
                  0,
                ),
              )}
            </span>
          </div>
        </div>
        <ConvertToSOPriceModal
          open={showConvertModal}
          onClose={() => setShowConvertModal(false)}
          items={row.items || []}
          onCreate={async (prices) => {
            const res = await ConvertSTToSO(row.id, prices);

            if (res?.success) {
              setShowConvertModal(false);
              showToast({
                type: ToastType.success,
                text: res.data?.message || "ST converted to SO successfully",
              });
              history.goBack();
              return;
            } else {
              showToast({
                type: ToastType.error,
                text: res?.error || "Failed to convert ST to SO",
              });
              console.error(res?.error);
            }
          }}
        />
      </div>
    </>
  );
};

const ReplacementDialog: React.FC<DialogProps> = ({
  row,
  onClose,
  onRecordReceipt,
}) => {
  const dueDate = getDueDate(row);

  return (
    <>
      <div
        id="warehouse-inward-awaiting-replacement-modal"
        className="fixed inset-0 bg-black bg-opacity-50 z-50 overflow-y-auto flex items-start justify-center p-4 py-10"
      >
        <div className="relative bg-white rounded-lg shadow-lg w-full max-w-4xl p-4 wh-modal-xs">
          <button
            id="warehouse-inward-awaiting-replacement-modal-close"
            className="absolute right-4 top-4"
            onClick={onClose}
          >
            <Icon path={mdiClose} size={0.8} />
          </button>

          <div className="flex items-center gap-3 flex-wrap pr-8">
            <h2 className="font-semibold text-xl">{row.id}</h2>

            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 text-xs rounded-full bg-red-100 text-red-600 border border-red-300">
              <Icon path={mdiSwapHorizontal} size={0.6} />
              Replacement
            </span>

            {row.status && (
              <span
                className={`inline-flex items-center gap-1 px-2.5 py-0.5 text-xs rounded-full border font-semibold ${getInwardStatusClass(
                  getReplacementInwardStatus(row),
                )}`}
              >
                {getReplacementInwardStatus(row)}
              </span>
            )}
          </div>

          <div className="mt-6 rounded-lg border bg-card p-4">
            <div className="flex gap-3 items-center">
              <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
                <Icon path={mdiCartOutline} size={0.8} className="text-white" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">
                  Linked Reference
                </p>
                <p className="font-semibold">{getLinkedRef(row)}</p>
              </div>
            </div>
          </div>

          <div className="mt-6 rounded-lg border bg-card p-6">
            <h3 className="flex items-center gap-2 font-medium text-sm mb-3">
              <Icon path={mdiMapMarker} size={0.8} className="text-green-600" />
              Party Details
            </h3>
            <p className="font-semibold">{getPartyName(row)}</p>
            <p className="text-sm text-muted-foreground">
              {row.from_warehouse_name || "--"}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4 mt-6">
            <div className="rounded-lg border bg-card p-4">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Icon
                  path={mdiCalendar}
                  size={0.8}
                  className="text-purple-600"
                />
                Expected Date
              </div>
              <p className="font-semibold">
                {dueDate ? formatDateYYYYMMDD(dueDate) : "--"}
              </p>
            </div>

            <div className="rounded-lg border bg-card p-4">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Icon path={mdiAccount} size={0.8} className="text-teal-600" />
                Requested By
              </div>
              <p className="font-semibold">{row.created_by_name || "--"}</p>
            </div>
          </div>

          <div className="mt-6 rounded-lg border bg-card p-6">
            <h3 className="flex items-center gap-2 text-sm font-medium mb-4">
              <Icon
                path={mdiPackageVariant}
                size={0.8}
                className="text-orange-600"
              />
              Items ({(row.faulty_items || []).length})
            </h3>

            <div className="space-y-3">
              {(row.faulty_items || []).map((item) => (
                <div
                  className="border rounded-lg p-3"
                  key={`${row.id}-${item.inventoryItemCode}`}
                >
                  <div className="flex justify-between">
                    <div>
                      <p className="font-medium">{item.itemName}</p>
                      <p className="text-xs text-muted-foreground">
                        {item.inventoryItemCode}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold">Qty: {item.requestedQty}</p>
                      <p className="text-xs text-muted-foreground">
                        Rs {formatAmountWithCommas(getItemValue(item))}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-6 flex justify-between border-t pt-4">
            <span className="text-muted-foreground">Total Value</span>
            <span className="text-xl font-bold">
              Rs{" "}
              {formatAmountWithCommas(
                (row.faulty_items || []).reduce(
                  (sum, item) => sum + getItemValue(item),
                  0,
                ),
              )}
            </span>
          </div>

          <div className="mt-6 flex justify-end gap-2">
            <button
              id="warehouse-inward-awaiting-replacement-modal-close-btn"
              onClick={onClose}
              className="border px-3 py-2 rounded-md"
              type="button"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

const AwaitingReturnsCardList: React.FC<AwaitingReturnsCardListProps> = ({
  awaitingReturns,
  updatesAllowed = true,
}) => {
  const navigate = useHistory();
  const [selectedReturn, setSelectedReturn] =
    useState<AwaitingReturnModel | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  const sortedReturns = useMemo(
    () =>
      [...(awaitingReturns || [])].sort(
        (a, b) =>
          new Date(b.created_at || "").getTime() -
          new Date(a.created_at || "").getTime(),
      ),
    [awaitingReturns],
  );
  const hasMoreThanThree = sortedReturns.length > 3;
  const visibleReturns = useMemo(
    () => (isExpanded ? sortedReturns : sortedReturns.slice(0, 3)),
    [isExpanded, sortedReturns],
  );

  const getBadgeClass = (subType?: string) => {
    const normalizedSubtype = (subType || "").toLowerCase();
    return (
      subtypePillMap[normalizedSubtype] ||
      "bg-blue-100 text-blue-700 border border-blue-300"
    );
  };

  const handleRecordReceipt = (row: AwaitingReturnModel) => {
    setSelectedReturn(null);
    navigate.push(`${urls.warehouse_inward}/receive/ST/${row.id}`);
  };

  const currentType = normalizeStockTransferSubtype(selectedReturn?.st_subtype);

  return (
    <>
      <div
        id="warehouse-inward-awaiting-returns-list"
        className="rounded-lg border bg-white shadow-sm border-l-4 border-l-primary"
      >
        <div className="flex flex-col space-y-1.5 p-5 pb-3">
          <h3 className="text-base font-semibold flex items-center gap-2">
            <Icon path={mdiPackageVariant} size={0.8} className="text-blue" />
            Awaiting Returns
            <span className="ml-1 inline-flex items-center rounded-full bg-gray-200 px-2.5 py-0.5 text-xs font-semibold">
              {sortedReturns.length}
            </span>
          </h3>
        </div>

        <div className="p-6 pt-0 space-y-3">
          {sortedReturns.length === 0 && (
            <p className="text-sm text-muted-foreground">
              No awaiting returns.
            </p>
          )}

          {visibleReturns.map((row) => {
            const subType = getSubtypeLabel(row.st_subtype);
            const isReplacementSubtype = isReplacementStockTransferSubtype(
              row.st_subtype,
            );
            const overdueDays = row.overdue_days || 0;
            const isOverdue = Boolean(row.is_overdue) || overdueDays > 0;
            const linkedRef = getLinkedRef(row);
            const dueDate = getDueDate(row);
            const inwardStatus = getReplacementInwardStatus(row);

            return (
              <div
                key={row.id}
                id={`warehouse-inward-awaiting-return-card-${row.id}`}
                className={`flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 rounded-lg border gap-3 ${
                  isOverdue ? "bg-red-100 border-red-300" : "bg-gray-100"
                }`}
              >
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm">{row.id}</span>

                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full ${getBadgeClass(
                        row.st_subtype,
                      )}`}
                    >
                      <Icon
                        path={
                          isReplacementSubtype ? mdiRefresh : mdiSwapHorizontal
                        }
                        size={0.5}
                      />
                      {subType}
                    </span>

                    {isReplacementSubtype && (
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full border font-semibold ${getInwardStatusClass(
                          inwardStatus,
                        )}`}
                      >
                        {inwardStatus}
                      </span>
                    )}

                    {isOverdue && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-red-500 text-white">
                        <Icon path={mdiAlertOutline} size={0.6} />
                        {overdueDays}d overdue
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
                    <span>{getPartyName(row)}</span>
                    <span>|</span>

                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 border rounded text-xs">
                      <Icon path={mdiCartOutline} size={0.6} />
                      {linkedRef}
                    </span>

                    <span>|</span>
                    <span>
                      Due: {dueDate ? formatDateYYYYMMDD(dueDate) : "--"}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    id={`warehouse-inward-awaiting-return-view-${row.id}`}
                    onClick={() => setSelectedReturn(row)}
                    className=" bg-white h-7 w-8 flex items-center justify-center rounded-lg hover:bg-yellow-400"
                    title="View"
                  >
                    <Icon path={mdiEyeOutline} size={0.7} />
                  </button>

                  {updatesAllowed && (
                    <button
                      id={`warehouse-inward-awaiting-return-record-${row.id}`}
                      onClick={() => handleRecordReceipt(row)}
                      className=" bg-white h-7 px-3 flex items-center gap-1 border rounded-lg hover:bg-yellow-400 text-sm"
                    >
                      <Icon path={mdiClipboardCheckOutline} size={0.8} />
                      Record Receipt
                    </button>
                  )}
                </div>
              </div>
            );
          })}

          {hasMoreThanThree && (
            <div className="flex justify-center pt-1">
              <button
                id="warehouse-inward-awaiting-returns-toggle-expand"
                type="button"
                className="text-sm font-semibold text-blue-600 hover:text-blue-700 hover:underline"
                onClick={() => setIsExpanded((prev) => !prev)}
              >
                {isExpanded
                  ? "Show less"
                  : `Open more (${sortedReturns.length - 3} more)`}
              </button>
            </div>
          )}
        </div>
      </div>

      {selectedReturn && currentType === StockTransferSubtype.RETURNABLE && (
        <ReturnableDialog
          row={selectedReturn}
          onClose={() => setSelectedReturn(null)}
          onRecordReceipt={handleRecordReceipt}
        />
      )}

      {selectedReturn && currentType !== StockTransferSubtype.RETURNABLE && (
        <ReplacementDialog
          row={selectedReturn}
          onClose={() => setSelectedReturn(null)}
          onRecordReceipt={handleRecordReceipt}
        />
      )}
    </>
  );
};

export default AwaitingReturnsCardList;
